"use client";

import { useState, useEffect } from "react";
import { getCountryRules } from "@/lib/country/registry";
import { getCalculatorConfig } from "@/lib/constants/calculators";
import CalculatorLayout from "./CalculatorLayout";
import { SITE_AUTHOR, getCalculatorReviewMonth } from "@/lib/constants/eeat";
import CurrencyInput from "../ui/CurrencyInput";
import PercentInput from "../ui/PercentInput";
import NumberInput from "../ui/NumberInput";
import SelectInput from "../ui/SelectInput";
import ResultCard from "../ui/ResultCard";
import BreakdownTable, { BreakdownRow } from "../ui/BreakdownTable";
import Chart from "../ui/Chart";
import ProjectionTable from "../ui/ProjectionTable";
import { executeCalculation } from "./calculationEngine";
import { buildChartData, getProjectionTableData } from "./chartDataBuilder";
import { getCalculatorSeoContent } from "@/data/calculator/calculator-seo-content";
import { getCalculatorEducationalContent } from "@/data/calculator/calculator-educational-db";
import { GLOSSARY_ALIASES, normalizeGlossaryKey } from "@/lib/glossary/glossary-aliases";
import { slugify } from "@/lib/utils/slugify";
import FormulaRenderer from "./FormulaRenderer";
import CalculatorEducation from "./CalculatorEducation";
import { renderInlineFinanceText as parseInlineMarkdown } from "./renderInlineFinanceText";

interface CalculatorPageTemplateProps {
  calculatorSlug: string;
  countryCode: string;
  relatedBlogPosts?: Array<{ title: string; slug: string; summary: string; images?: Array<{ url: string; alt: string }> }>;
  // Computed server-side (see CalculatorLayout.ymalItems)
  ymalItems?: import("@/lib/blog/ymal-mapper").YouMightAlsoLikeItem[];
}

export default function CalculatorPageTemplate({
  calculatorSlug,
  countryCode,
  relatedBlogPosts = [],
  ymalItems = [],
}: CalculatorPageTemplateProps) {
  const config = getCalculatorConfig(calculatorSlug);
  const rules = getCountryRules(countryCode);

  if (!config) {
    return <div className="container py-12">Calculator configuration not found.</div>;
  }

  // Set up inputs state
  const initialInputs: Record<string, any> = {};
  config.inputs.forEach((input) => {
    initialInputs[input.id] = input.defaultValue;
  });

  const [inputs, setInputs] = useState<Record<string, any>>(initialInputs);
  const [results, setResults] = useState<Record<string, any>>({});

  const handleInputChange = (id: string, value: any) => {
    setInputs((prev) => {
      const next = { ...prev, [id]: value };
      
      // Validation for KVdR Calculator
      if (calculatorSlug === "kvdr-9-10-rule-eligibility-calculator") {
        const r = Number(next.retirementAge ?? 67);
        const s = Number(next.workingStartAge ?? 20);
        
        // Ensure start age is always less than retirement age
        if (id === "workingStartAge" && value >= next.retirementAge) {
          next.workingStartAge = next.retirementAge - 1;
        } else if (id === "retirementAge" && value <= next.workingStartAge) {
          next.retirementAge = next.workingStartAge + 1;
        }
        
        const finalMaxGkvYears = Math.max(0, (next.retirementAge - next.workingStartAge) / 2);
        
        // Cap gkvYearsSecondHalf to finalMaxGkvYears
        if (Number(next.gkvYearsSecondHalf ?? 15) > finalMaxGkvYears) {
          next.gkvYearsSecondHalf = Number(finalMaxGkvYears.toFixed(2));
        }
      }
      
      return next;
    });
  };

  // Run calculation whenever inputs change
  useEffect(() => {
    const calculated = executeCalculation(config, inputs, countryCode);
    setResults(calculated);
  }, [inputs, config, countryCode]);

  // Build chart data
  const chartData = buildChartData(results, config, inputs);

  // Extract projection table data
  const { hasProjection, headers: projectionHeaders, rows: projectionRows, title: projectionTitle } = getProjectionTableData(results, config, inputs);

  // Create table rows for results
  const getTableRows = (): BreakdownRow[] => {
    if (!results || Object.keys(results).length === 0) return [];
    
    // Custom mapping for breakdown tables
    if (results.breakdown) {
      return results.breakdown.map((item: any) => ({
        label: item.label,
        value: item.amount,
      }));
    }

    // Default: map all numerical outputs to rows
    const rows: BreakdownRow[] = [];
    config.outputs.forEach((out) => {
      const val = results[out.id];
      if (typeof val === "number" && out.type === "currency") {
        rows.push({
          label: out.name,
          value: val,
        });
      }
    });

    return rows;
  };

  const getWorkedExampleParagraphs = (): string[] => {
    return Array.isArray(eduContent?.workedExample) ? eduContent.workedExample : [eduContent?.workedExample || ""];
  };

  const tableRows = getTableRows();

  const seoContent = getCalculatorSeoContent(calculatorSlug, countryCode, config.name, config.description);
  const eduContent = getCalculatorEducationalContent(calculatorSlug);
  const year = 2026;

  const getGlossaryLink = (termName: string): string | null => {
    if (!rules.glossary || rules.glossary.length === 0) return null;
    const target = normalizeGlossaryKey(termName);
    if (!target) return null;

    // 1) Exact normalized match on the term name (the safe, precise case).
    let matched = rules.glossary.find((g) => normalizeGlossaryKey(g.term) === target);

    // 2) Curated alias whitelist — replaces the old bidirectional substring
    //    match that mislinked (e.g. "Tax" -> "Income Tax"). Aliases resolve to a
    //    canonical term name, still requiring an exact match against the data.
    if (!matched) {
      const aliasName = GLOSSARY_ALIASES[target];
      if (aliasName) {
        const aliasTarget = normalizeGlossaryKey(aliasName);
        matched = rules.glossary.find((g) => normalizeGlossaryKey(g.term) === aliasTarget);
      }
    }

    if (matched) {
      // Uses the shared slugify() so this link always matches the URL the
      // glossary route generates (P7a centralization + 301s handle old URLs).
      const slug = slugify(matched.term);
      return `/${countryCode.toLowerCase()}/glossary/${slug}`;
    }
    return null;
  };

  const renderMarkdown = (markdownText: string): React.ReactNode => {
    if (!markdownText) return null;
    const paragraphs = markdownText.split(/\n\n+/);
    return (
      <>
        {paragraphs.map((para, pIdx) => {
          const lines = para.split('\n');
          if (
            lines.length > 1 &&
            lines.every(line => {
              const trimmed = line.trim();
              return trimmed.startsWith('*') || trimmed.startsWith('-') || /^\d+\./.test(trimmed);
            })
          ) {
            return (
              <ul key={pIdx} style={{ paddingLeft: "var(--space-6)", marginBottom: "var(--space-4)", listStyleType: "disc" }}>
                {lines.map((line, lIdx) => {
                  const cleanLine = line.trim().replace(/^[\*\-]\s*/, '').replace(/^\d+\.\s*/, '');
                  return (
                    <li key={lIdx} style={{ marginBottom: "var(--space-2)" }}>
                      {parseInlineMarkdown(cleanLine)}
                    </li>
                  );
                })}
              </ul>
            );
          }
          return (
            <p key={pIdx} style={{ marginBottom: "var(--space-4)", lineHeight: 1.6 }}>
              {parseInlineMarkdown(para)}
            </p>
          );
        })}
      </>
    );
  };

  const defaultGuide = (
    <div>
      {/* Quick Summary Block */}
      <div className="quick-summary-flat" style={{ marginBottom: "var(--space-8)" }}>
        <p className="lead-text" style={{ margin: 0, color: "var(--text-primary)", fontStyle: "italic" }}>
          {seoContent.intro}
        </p>
      </div>

      {seoContent.sections.map((sec, idx) => (
        <div key={idx} style={{ marginBottom: "var(--space-8)" }}>
          <h3>{sec.heading}</h3>
          <div style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>{renderMarkdown(sec.body)}</div>
        </div>
      ))}

      {/* Teaching layer: pain-point education sections with semantic charts
          (PRD 01 Epics E/G/J). Required on every calculator since the
          education[] migration completed. */}
      {eduContent?.education && eduContent.education.length > 0 && (
        <CalculatorEducation sections={eduContent.education} />
      )}

      {/* 1. Mathematical Formula Block */}
      {eduContent && (
        <div className="formula-flat-section" style={{ marginBottom: "var(--space-8)" }}>
          <h3>{eduContent.formulaName}</h3>
          <div style={{ color: "var(--text-secondary)", fontSize: "var(--text-base)", lineHeight: 1.6, marginBottom: "var(--space-4)" }}>
            {Array.isArray(eduContent.formulaExplanation) ? (
              eduContent.formulaExplanation.map((para, idx) => (
                <p key={idx} style={{ marginBottom: "var(--space-3)" }}>{para}</p>
              ))
            ) : (
              <p>{eduContent.formulaExplanation}</p>
            )}
          </div>
          <FormulaRenderer equation={eduContent.formulaEquation} displayMode={true} />
          <h4 style={{ color: "var(--text-primary)", marginTop: "var(--space-4)", marginBottom: "var(--space-2)" }}>Calculation Steps:</h4>
          <ol style={{ paddingLeft: "var(--space-5)", color: "var(--text-secondary)", lineHeight: 1.6 }}>
            {eduContent.formulaSteps.map((step, idx) => (
              <li key={idx} style={{ marginBottom: "var(--space-2)" }}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* 2. Worked Example Block */}
      {eduContent && (
        <div className="example-flat-section" style={{ marginBottom: "var(--space-8)" }}>
          <h3>Worked example</h3>
          <div style={{ color: "var(--text-secondary)", lineHeight: 1.6, fontSize: "var(--text-base)" }}>
            {getWorkedExampleParagraphs().map((para, idx) => (
              <p key={idx} style={{ marginBottom: "var(--space-3)" }}>{para}</p>
            ))}
          </div>
        </div>
      )}

      {/* 7. Parameters Glossary Table */}
      {eduContent && (
        <div className="glossary-table-flat-section" style={{ marginBottom: 0 }}>
          <h3>Input definitions</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: "var(--text-base)", marginBottom: "var(--space-4)", lineHeight: 1.6 }}>
            Review the glossary of terms used in the calculation model below. Click on highlighted links to read more in-depth definitions in our financial glossary:
          </p>
          <div style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: "var(--radius-md)", marginTop: "var(--space-4)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "var(--text-sm)" }}>
              <thead>
                <tr style={{ backgroundColor: "var(--bg-muted)", borderBottom: "1px solid var(--border-color)" }}>
                  <th style={{ padding: "var(--space-3) var(--space-4)", fontWeight: "var(--fw-bold)", color: "var(--text-primary)", width: "30%" }}>Parameter</th>
                  <th style={{ padding: "var(--space-3) var(--space-4)", fontWeight: "var(--fw-bold)", color: "var(--text-primary)" }}>Definition & Context</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(eduContent.parameterDefinitions).map(([key, def]) => {
                  const glossaryLink = getGlossaryLink(def.name);
                  return (
                    <tr key={key} style={{ borderBottom: "1px solid var(--border-color)" }}>
                      <td style={{ padding: "var(--space-3) var(--space-4)", fontWeight: "var(--fw-semibold)", color: "var(--text-primary)" }}>
                        {glossaryLink ? (
                          <a href={glossaryLink} style={{ color: "var(--primary)", textDecoration: "underline", fontWeight: "var(--fw-bold)" }}>{def.name}</a>
                        ) : (
                          def.name
                        )}
                      </td>
                      <td style={{ padding: "var(--space-3) var(--space-4)", color: "var(--text-secondary)", lineHeight: 1.6 }}>
                        {def.definition}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const defaultFaqs = seoContent.faqs.map((faq) => ({
    title: faq.question,
    content: <span>{parseInlineMarkdown(faq.answer)}</span>,
  }));

  // The sole call site (the [slug] route) passes neither a guide override nor
  // custom FAQs — the dead optional props were removed with the legacy fields.

  // Search-aligned title & description query from localized content
  const pageTitle = seoContent.seoTitle
    ? seoContent.seoTitle.replace(/{country}/g, rules.meta.name).replace(/{year}/g, "2026")
    : config.seoTitle
      ? config.seoTitle.replace(/{country}/g, rules.meta.name).replace(/{year}/g, "2026")
      : `${rules.meta.name} ${config.name.replace(/\s+Calculator$/i, "")} ${year} | Calculate in 60s`;

  const pageDescription = seoContent.metaDescription
    ? seoContent.metaDescription.replace(/{country}/g, rules.meta.name).replace(/{year}/g, "2026")
    : config.description;

  return (
    <CalculatorLayout
      title={pageTitle}
      description={pageDescription}
      countryCode={countryCode}
      category={config.category}
      educationalContent={defaultGuide}
      faqs={defaultFaqs}
      lastUpdated={getCalculatorReviewMonth(calculatorSlug)}
      authorName={SITE_AUTHOR.name}
      authorCredentials={SITE_AUTHOR.credentials}
      calculatorSlug={calculatorSlug}
      relatedTools={config.relatedTools}
      relatedBlogPosts={relatedBlogPosts}
      ymalItems={ymalItems}
    >
      <div className="calculator-widget-wrapper">
        <div className="calculator-widget-grid">
          <div className="calculator-inputs-card">
            {/* h2: first heading after the page h1 — the interactive tool is
                its own top-level section (was h3, a level skip under the h1). */}
            <h2 className="widget-title">Adjust Simulator Inputs</h2>
            <div className="inputs-stack">
            {config.inputs.map((input) => {
              if (input.id === "filingStatus" && countryCode.toLowerCase() === "us") {
                input.options = [
                  { label: "Single", value: "single" },
                  { label: "Married Filing Jointly", value: "married_jointly" },
                  { label: "Head of Household", value: "head_of_household" }
                ];
              }

              if (input.id === "taxRegime" && countryCode.toLowerCase() !== "in") {
                return null;
              }
              if (input.id === "taxRegime" && countryCode.toLowerCase() === "in") {
                input.options = [
                  { label: "New Tax Regime (Standard)", value: "new" },
                  { label: "Old Tax Regime", value: "old" }
                ];
              }

              if (input.id === "filingStatus" && countryCode.toLowerCase() !== "us") {
                return null;
              }

              return (
                <div key={input.id} className="input-item">
                  {input.type === "currency" && (
                    <CurrencyInput
                      id={input.id}
                      label={input.name}
                      value={inputs[input.id]}
                      onChange={(val) => handleInputChange(input.id, val)}
                      currencySymbol={rules.meta.currency.symbol}
                      currencyCode={rules.meta.currency.code}
                      locale={rules.meta.locale}
                    />
                  )}

                  {input.type === "percent" && (
                    <PercentInput
                      id={input.id}
                      label={input.name}
                      value={inputs[input.id]}
                      onChange={(val) => handleInputChange(input.id, val)}
                      min={input.min}
                      max={input.max}
                      step={input.step}
                    />
                  )}

                  {input.type === "number" && (
                    <NumberInput
                      id={input.id}
                      label={input.name}
                      value={inputs[input.id]}
                      onChange={(val) => handleInputChange(input.id, val)}
                      min={input.min}
                      max={
                        calculatorSlug === "kvdr-9-10-rule-eligibility-calculator" && input.id === "gkvYearsSecondHalf"
                          ? Math.max(0, ((inputs.retirementAge ?? 67) - (inputs.workingStartAge ?? 20)) / 2)
                          : input.max
                      }
                      step={input.step}
                    />
                  )}

                  {input.type === "select" && (
                    <SelectInput
                      id={input.id}
                      label={input.name}
                      value={inputs[input.id]}
                      onChange={(val) => handleInputChange(input.id, val)}
                      options={input.options || []}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="calculator-results-pane">
          <div className="results-cards-grid">
            {config.outputs.map((out) => {
              const val = results[out.id];
              if (val === undefined) return null;

              if (out.id === "exceedsThreshold") {
                return (
                  <ResultCard
                    key={out.id}
                    label={out.name}
                    value={val ? "Yes" : "No"}
                    locale={rules.meta.locale}
                    color={val ? "danger" : "primary"}
                  />
                );
              }

              return (
                <ResultCard
                  key={out.id}
                  label={out.name}
                  value={val}
                  prefix={out.type === "currency" ? rules.meta.currency.symbol : ""}
                  suffix={out.type === "percent" ? "%" : ""}
                  locale={rules.meta.locale}
                  color={
                    out.id.toLowerCase().includes("takehome") || out.id.toLowerCase().includes("savings") || out.id.toLowerCase().includes("gain")
                      ? "primary"
                      : out.id.toLowerCase().includes("tax") || out.id.toLowerCase().includes("interest")
                      ? "danger"
                      : "secondary"
                  }
                />
              );
            })}
          </div>

          {chartData && (
            <div className="chart-box card" style={{ marginTop: "var(--space-4)" }}>
              <Chart
                type={chartData.chartType || "bar"}
                data={chartData}
                title={`${config.name} Breakdown`}
              />
            </div>
          )}
        </div>
      </div>

      {(tableRows.length > 0 || hasProjection) && (
        <div className="visualization-grid" style={{ marginTop: "var(--space-6)" }}>
          {hasProjection && projectionRows.length > 0 && (
            <div className="projection-table-box">
              <ProjectionTable
                headers={projectionHeaders}
                rows={projectionRows}
                title={projectionTitle || (projectionHeaders[0] === "Year" ? `${config.name} Year-by-Year Detailed Projection` : `${config.name} Detailed Breakdown`)}
              />
            </div>
          )}
          {!hasProjection && tableRows.length > 0 && (
            <div className="table-box">
              <h4 className="chart-title">Detailed Calculation Breakdown</h4>
              <BreakdownTable
                rows={tableRows}
                currencySymbol={rules.meta.currency.symbol}
                locale={rules.meta.locale}
                total={calculatorSlug === 'zzp-tax-deduction-take-home-calculator' ? undefined : (results.totalTax || results.grossIncome)}
              />
            </div>
          )}
        </div>
      )}
    </div>

      <style jsx>{`
        .calculator-widget-wrapper {
          background: var(--bg-muted);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: var(--space-6);
          margin-bottom: var(--space-6);
        }
        @media (max-width: 767px) {
          .calculator-widget-wrapper {
            padding: var(--space-4);
            border-radius: var(--radius-md);
          }
        }
        .calculator-widget-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: var(--space-4);
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }
        @media (min-width: 1024px) {
          .calculator-widget-grid {
            grid-template-columns: minmax(300px, 380px) 1fr;
            gap: var(--space-5);
          }
        }
        .calculator-inputs-card {
          background: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-4);
          min-width: 0;
        }
        .calculator-results-pane {
          min-width: 0;
          overflow: hidden;
          max-width: 100%;
        }
        .widget-title {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin-bottom: var(--space-3);
          padding-bottom: var(--space-2);
          border-bottom: 1px solid var(--border-color);
        }
        .inputs-stack {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
        }
        .input-item {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
          min-width: 0;
          width: 100%;
        }
        .results-cards-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: var(--space-2);
          margin-bottom: var(--space-4);
          width: 100%;
        }
        @media (min-width: 640px) {
          .results-cards-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .visualization-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: var(--space-4);
          width: 100%;
          min-width: 0;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .visualization-grid.has-both {
            grid-template-columns: 1fr 1.2fr;
            align-items: start;
          }
        }
        .chart-box {
          background-color: var(--bg-input) !important;
          border: 1px solid var(--border-color) !important;
          border-radius: var(--radius-md) !important;
          padding: var(--space-3);
          height: 320px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          overflow: hidden;
        }
        @media (max-width: 767px) {
          .chart-box {
            height: 230px;
            padding: var(--space-2);
          }
        }
        @media (max-width: 480px) {
          .chart-box {
            height: 200px;
          }
        }
        .projection-table-box {
          width: 100%;
          min-width: 0;
          overflow: hidden;
        }
        .table-box {
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-3);
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          min-width: 0;
          overflow: hidden;
        }
        @media (max-width: 480px) {
          .table-box {
            padding: var(--space-2);
          }
        }
        .chart-title {
          font-family: var(--font-heading);
          font-size: var(--text-xs);
          font-weight: var(--fw-extrabold);
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          text-align: left;
          margin-bottom: var(--space-2);
          padding-bottom: var(--space-2);
          border-bottom: 1px solid var(--border-color);
          width: 100%;
        }
      `}</style>
    </CalculatorLayout>
  );
}
